import { useMemo } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { KbGenerationFieldStep } from "../../types/gameRuleKbGenerationTemplate";

type StepRow = {
  readonly id: string;
  readonly name: string;
};

type GameRuleTemplateStepsSidebarProps = {
  readonly steps: readonly KbGenerationFieldStep[];
  readonly selectedStepId: string | null;
  readonly onSelectStep: (stepId: string) => void;
  readonly isLoading?: boolean;
};

export const GameRuleTemplateStepsSidebar = ({
  steps,
  selectedStepId,
  onSelectStep,
  isLoading,
}: GameRuleTemplateStepsSidebarProps) => {
  const rows: StepRow[] = useMemo(() => steps.map((s) => ({ id: s.id, name: s.name })), [steps]);

  const columns: TableColumn<StepRow>[] = useMemo(
    () => [
      {
        name: "Section",
        selector: (row) => row.name,
        sortable: false,
        grow: 1,
        cell: (row) => (
          <button
            type="button"
            onClick={() => onSelectStep(row.id)}
            className={`w-full text-left text-sm font-medium transition-colors ${
              row.id === selectedStepId
                ? "text-primary-500"
                : "text-gray-700 hover:text-primary-600"
            }`}
          >
            <span className="line-clamp-2">{row.name}</span>
          </button>
        ),
      },
    ],
    [selectedStepId, onSelectStep],
  );

  return (
    <div className="flex w-72 shrink-0 flex-col border-r border-gray-200 bg-white">
      <div className="border-gray-200 p-4">
        <p className="text-sm font-semibold text-gray-900">
          {steps.length} section{steps.length !== 1 ? "s" : ""}
        </p>
      </div>
      <div className="flex-1 overflow-auto mr-4">
        <DataTable
          columns={columns}
          data={rows}
          customStyles={dataTableCustomStyles}
          striped={false}
          progressPending={isLoading}
          noDataComponent={
            <div className="p-8 text-center text-sm text-gray-500">
              No sections in this template.
            </div>
          }
          highlightOnHover
          onRowClicked={(row) => onSelectStep(row.id)}
        />
      </div>
    </div>
  );
};
